import React from 'react';
import { Button } from '@odin-labs/components';
import {
  ChangeType,
  JobsiteWorker,
  useGetFormSubmissionJobsiteWorkersQuery,
} from 'apollo/generated/client-operations';
import { Change, EditType } from 'types';
import { useModalState } from 'utils';
import { pluralize } from 'utils/pluralize';
import { UserHardHatIcon } from 'components/icons';
import { AlertService } from 'components/alertService';
import {
  EditableFormSubmissionWorker,
  EditableEnhancedFormSubmissionWorker,
  EnhancedFormSubmissionWorker,
} from 'containers/jobsiteFormSubmission/types';
import { AddFormSubmissionWorkerModal } from 'containers/jobsiteFormSubmission/components/addFormSubmissionWorkerModal';
import { EditFormSubmissionWorkerModal } from './modals';
import { FormSubmissionWorker } from './FormSubmissionWorker';
import { FormSubmissionWorkersProps } from './types';

export const toEditableJobsiteFormSubmissionWorker = ({
  jfsWorker,
}: {
  jfsWorker: EnhancedFormSubmissionWorker;
}): EditableEnhancedFormSubmissionWorker => {
  const { jobsiteWorker: jw, historicalData, ...restJfsWorker } = jfsWorker;
  const { jobsiteWorkerAccess: jwAccess } = historicalData ?? {};

  return {
    ...restJfsWorker,
    jobsiteWorker: jw,
    jobsitesInfo: [
      {
        jobsiteWorker: jw as JobsiteWorker,
        jobsiteWorkerId: jw.jobsiteWorkerId,
        trade: jw.trade,
        title: jw.jobTitle,
        isJobsiteAccessAllowed: jwAccess?.isAllowedNow ?? false,
        jobsiteName: jw.jobsiteContractor.jobsite.name,
        contractorName: jw.jobsiteContractor.contractor?.organization?.name,
      },
    ],
  };
};

const getWorkerId = (jfsWorker: EditableEnhancedFormSubmissionWorker): string =>
  jfsWorker.jobsiteWorker.contractorWorker.worker.workerId;

export const FormSubmissionWorkers = React.forwardRef<
  HTMLDivElement,
  FormSubmissionWorkersProps
>((props, ref): React.ReactElement => {
  const {
    value,
    onChange,
    jobsiteFormSubmissionId,
    associationType,
    mode = 'edit',
    showInitialSearchResults,
  } = props;
  const allowWorkerEdit = mode === 'edit';

  const {
    value: modalValue,
    isModalOpen,
    openModal,
    closeModal,
    resetModalValue,
  } = useModalState<Change<EditableEnhancedFormSubmissionWorker>>(null);

  const { data, loading } = useGetFormSubmissionJobsiteWorkersQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteFormSubmissionId,
    variables: { id: jobsiteFormSubmissionId },
  });

  const { jobsiteId } =
    data?.getJobsiteFormSubmission.jobsiteForm.jobsite ?? {};

  const jfsWorkersById = Object.fromEntries(
    data?.getJobsiteFormSubmission.jobsiteWorkers.edges.map(({ node }) => [
      node.id,
      toEditableJobsiteFormSubmissionWorker({ jfsWorker: node }),
    ]) ?? [],
  );
  const jfsWorkers: EditableEnhancedFormSubmissionWorker[] = loading
    ? []
    : value
        .map((v) => {
          if (v.changeType === ChangeType.Created) {
            return v as EditableEnhancedFormSubmissionWorker;
          }
          const jfsWorker = jfsWorkersById[v.id];
          return (
            jfsWorker && {
              ...jfsWorker,
              ...v,
              jobsiteWorker: { ...jfsWorker.jobsiteWorker, ...v.jobsiteWorker },
            }
          );
        })
        .filter(Boolean);
  const visibleJfsWorkers = jfsWorkers.filter(
    (jfsW) => jfsW.changeType !== ChangeType.Removed,
  );
  const addedWorkerIds = visibleJfsWorkers.map(getWorkerId);

  const updateValue = React.useCallback(
    (
      jfsWorker: EditableEnhancedFormSubmissionWorker,
      editType: EditType,
    ): void => {
      const { workerId } = jfsWorker.jobsiteWorker.contractorWorker.worker;
      let newValue: EditableFormSubmissionWorker[];
      if (editType === 'create') {
        const existingJfsW = jfsWorkers?.find(
          (jfsW) => getWorkerId(jfsW) === workerId,
        );
        // if jfsW has been previously removed then it will be updated
        if (existingJfsW?.changeType === ChangeType.Removed) {
          newValue = value.map((jfsW) =>
            // existing jfsW id is preserved
            jfsW.id !== existingJfsW.id
              ? jfsW
              : {
                  ...jfsW,
                  ...jfsWorker,
                  id: jfsW.id,
                  changeType: ChangeType.Updated,
                },
          );
        } else if (existingJfsW) {
          AlertService.alert('danger', '', 'Worker has already been assigned!');
        } else {
          // add new participant
          newValue = [
            ...value,
            { ...jfsWorker, changeType: ChangeType.Created, associationType },
          ];
        }
      } else {
        newValue = value.map((jfsW) =>
          jfsW.id !== jfsWorker.id ? jfsW : { ...jfsW, ...jfsWorker },
        );
      }

      if (newValue) {
        onChange?.(newValue);
      }
    },
    [associationType, onChange, value, jfsWorkers],
  );

  const updateValueAndCloseModal = React.useCallback(
    (
      jfsWorker: EditableEnhancedFormSubmissionWorker,
      editType: EditType,
    ): void => {
      updateValue(jfsWorker, editType);
      closeModal();
    },
    [updateValue, closeModal],
  );

  const removeParticipant = (
    jfsWorker: EditableEnhancedFormSubmissionWorker,
  ): void => {
    const newValue: EditableFormSubmissionWorker[] =
      jfsWorker.changeType === ChangeType.Created
        ? value.filter((p) => p.id !== jfsWorker.id)
        : value.map((p) =>
            p.id !== jfsWorker.id
              ? p
              : { ...p, changeType: ChangeType.Removed },
          );

    onChange?.(newValue);
  };

  const removeWorker = (workerId: string): void => {
    const jfsWorker = visibleJfsWorkers.find(
      (jfsW) => getWorkerId(jfsW) === workerId,
    );
    if (jfsWorker) {
      removeParticipant(jfsWorker);
    }
  };

  const workersList = (visibleJfsWorkers.length || null) && (
    <div className="odin-flex odin-flex-col odin-space-y-2">
      <span className="odin-text-xs odin-font-medium odin-uppercase odin-tracking-wide odin-text-gray-500">
        {pluralize(visibleJfsWorkers.length, 'worker')}
      </span>
      {/* one column on phones, two from tablet width */}
      <ul className="odin-grid odin-grid-cols-1 sm:odin-grid-cols-2 odin-gap-x-3 odin-gap-y-2">
        {visibleJfsWorkers.map((jfsWorker) => {
          return (
            <FormSubmissionWorker
              key={getWorkerId(jfsWorker)}
              jfsWorker={jfsWorker}
              showDetails={allowWorkerEdit}
              onRemoveIconClick={(): void => removeParticipant(jfsWorker)}
              onClick={
                allowWorkerEdit
                  ? (): void => openModal({ type: 'update', item: jfsWorker })
                  : undefined
              }
            />
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      <div ref={ref} className="odin-flex odin-flex-col odin-space-y-4">
        <Button
          text="Add Worker to Report"
          theme="dashed"
          className="odin-w-full odin-justify-center odin-h-12"
          icon={UserHardHatIcon}
          onClick={(): void => openModal({ type: 'create' })}
        />
        {workersList}
      </div>
      {allowWorkerEdit ? (
        <EditFormSubmissionWorkerModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          onClosed={resetModalValue}
          onConfirm={updateValueAndCloseModal}
          jobsiteId={jobsiteId}
          jobsiteFormSubmissionId={jobsiteFormSubmissionId}
          jfsWorker={modalValue?.item}
          associationType={associationType}
          showInitialSearchResults={showInitialSearchResults}
        />
      ) : (
        <AddFormSubmissionWorkerModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          onConfirm={
            mode === 'addMany' ? updateValue : updateValueAndCloseModal
          }
          jobsiteId={jobsiteId}
          jobsiteFormSubmissionId={jobsiteFormSubmissionId}
          associationType={associationType}
          mode={mode}
          addedWorkerIds={addedWorkerIds}
          onRemove={removeWorker}
          showInitialSearchResults={showInitialSearchResults}
        />
      )}
    </>
  );
});
